import React from 'react';
import { Bell, Check, ChevronDown, Search, SlidersHorizontal } from 'lucide-react';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useI18n } from '../i18n/useI18n';

// ─── Layout ──────────────────────────────────────────────────────────────────

export const PageShell: React.FC<{ children: React.ReactNode; wide?: boolean }> = ({ children }) => (
  <main className="min-h-screen w-full text-zinc-950">
    <div className="w-full px-5 py-7 sm:px-8">
      {children}
    </div>
  </main>
);

export const PageHeader: React.FC<{ title: string; eyebrow?: string; description?: string }> = ({ title, eyebrow, description }) => (
  <div className="mb-6 flex flex-col border-b border-zinc-200 pb-5">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <button
          className="grid h-8 w-8 place-items-center rounded-lg  bg-white text-zinc-500 lg:hidden outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
          aria-label="Toggle Sidebar"
        >
          <SlidersHorizontal size={15} aria-hidden="true" />
        </button>
        {eyebrow && <span className="text-sm text-zinc-400">{eyebrow} /</span>}
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-950">{title}</h1>
      </div>
      <div className="flex items-center gap-3">
        <LanguageSwitcher />
        <div className="flex items-center gap-1.5 leading-none select-none">
          <span className="text-xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-zinc-800 to-zinc-500">
            元基
          </span>
          <span className="text-xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-cyan-500">
            GEO
          </span>
          <span className="inline-flex items-center justify-center rounded-bl-lg rounded-br-sm rounded-tl-sm rounded-tr-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 px-1.5 py-1 text-[9px] font-bold uppercase leading-none text-white shadow-sm ring-1 ring-inset ring-white/20">
            AI
          </span>
        </div>
        <button
          type="button"
          aria-label="查看通知"
          className="relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-zinc-600 shadow-sm transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
        >
          <Bell size={15} aria-hidden="true" />
          <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-rose-500" />
        </button>
        <div className="h-8 w-8 overflow-hidden rounded-full bg-white/80 p-1 shadow-sm" aria-hidden="true">
          <div className="flex h-full w-full items-center justify-center rounded-full bg-gradient-to-br from-zinc-900 to-zinc-500 text-[11px] font-semibold text-white">
            AI
          </div>
        </div>
      </div>
    </div>
    {description && (
      <p className="mt-1.5 text-sm text-zinc-400">{description}</p>
    )}
  </div>
);

// ─── Filters ─────────────────────────────────────────────────────────────────

export interface FilterConfig {
  key: string;
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}

const FilterPill: React.FC<FilterConfig> = ({ label, options, value, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isFiltered = value !== options[0];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3.5 text-xs font-medium shadow-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1 ${
          isFiltered
            ? 'bg-zinc-900 text-white hover:bg-zinc-800'
            : 'bg-white text-zinc-600 hover:bg-zinc-50'
        }`}
      >
        <span>{isFiltered ? value : label}</span>
        <ChevronDown size={12} className={isFiltered ? 'text-zinc-300' : 'text-zinc-400'} aria-hidden="true" />
      </button>
      {open && (
        <div className="absolute left-0 z-30 mt-1.5 min-w-[160px] rounded-xl border border-zinc-200 bg-white py-1.5 shadow-lg">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => { onChange(opt); setOpen(false); }}
              className="flex w-full items-center gap-2 px-3.5 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
            >
              <Check size={12} className={opt === value ? 'text-zinc-900' : 'invisible'} aria-hidden="true" />
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const FilterBar: React.FC<{
  extra?: React.ReactNode;
  filters?: FilterConfig[];
}> = ({ extra, filters }) => {
  const { t } = useI18n();

  if (!filters) {
    return (
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {[t('filters.tradingbase'), t('filters.allTime'), t('filters.allTags'), t('filters.allModels'), t('filters.allTopics')].map((item) => (
          <button
            key={item}
            className="inline-flex h-8 items-center gap-1.5 rounded-full bg-white shadow-sm px-3.5 text-xs font-medium text-zinc-600 outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1 hover:bg-zinc-50 transition-colors"
          >
            <span>{item}</span>
            <ChevronDown size={12} className="text-zinc-400" aria-hidden="true" />
          </button>
        ))}
        {extra}
      </div>
    );
  }

  return (
    <div className="mb-5 flex flex-wrap items-center gap-2">
      {filters.map((f) => <FilterPill key={f.key} {...f} />)}
      {extra}
    </div>
  );
};

// ─── Cards ───────────────────────────────────────────────────────────────────

export const SectionCard: React.FC<{
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, subtitle, action, children, className = '' }) => (
  <section className={`rounded-xl bg-white shadow-sm ${className}`}>
    {(title || subtitle || action) && (
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-zinc-200 px-5 py-3.5">
        <div>
          {title && <h2 className="text-sm font-semibold tracking-tight text-zinc-950">{title}</h2>}
          {subtitle && <p className="mt-0.5 text-xs text-zinc-400">{subtitle}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </section>
);

export const SearchToolbar: React.FC<{
  children?: React.ReactNode;
  placeholder?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  ariaLabel?: string;
}> = ({
  children,
  placeholder = 'Search',
  value,
  onChange,
  name = 'search',
  ariaLabel = 'Search',
}) => (
  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-5 py-3">
    <label className="relative min-w-[240px] flex-1 sm:max-w-sm">
      <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -tranzinc-y-1/2 text-zinc-400" aria-hidden="true" />
      <input
        aria-label={ariaLabel}
        name={name}
        className="h-8 w-full rounded-lg  bg-white pl-8 pr-3 text-sm outline-none transition-colors focus-visible:border-zinc-300 focus-visible:ring-2 focus-visible:ring-zinc-200 placeholder:text-zinc-400"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoComplete="off"
        spellCheck={false}
      />
    </label>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

// ─── Buttons ─────────────────────────────────────────────────────────────────

export const ToolButton: React.FC<{ children?: React.ReactNode; label?: string; onClick?: () => void }> = ({
  children,
  label,
  onClick,
}) => (
  <button
    onClick={onClick}
    className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-white shadow-sm px-3 text-xs font-medium text-zinc-600 outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1 hover:bg-zinc-50 transition-colors"
  >
    {children || label}
  </button>
);

export const IconTool: React.FC<{ label: string; children?: React.ReactNode }> = ({ label, children }) => (
  <button
    aria-label={label}
    title={label}
    className="grid h-8 w-8 place-items-center rounded-lg bg-white shadow-sm text-zinc-500 outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 hover:bg-zinc-50 transition-colors"
  >
    {children || <span className="text-sm font-bold leading-none">···</span>}
  </button>
);

// ─── Data display ─────────────────────────────────────────────────────────────

export const Delta: React.FC<{ value?: string; tone?: 'up' | 'down' | 'flat' }> = ({ value = '–', tone = 'flat' }) => {
  const colors =
    tone === 'up' ? 'text-emerald-600' : tone === 'down' ? 'text-rose-500' : 'text-zinc-300';
  return <span className={`ml-1 text-[11px] tabular-nums ${colors}`}>{value}</span>;
};

export const Badge: React.FC<{
  children: React.ReactNode;
  tone?: 'slate' | 'blue' | 'green' | 'amber' | 'rose' | 'purple';
}> = ({ children, tone = 'slate' }) => {
  const colors = {
    slate:  'border-zinc-200   bg-zinc-50   text-zinc-600',
    blue:   'border-sky-200     bg-sky-50     text-sky-700',
    green:  'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber:  'border-amber-200   bg-amber-50   text-amber-700',
    rose:   'border-rose-200    bg-rose-50    text-rose-700',
    purple: 'border-violet-200  bg-violet-50  text-violet-700',
  }[tone];
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${colors}`}>
      {children}
    </span>
  );
};

export const DomainIdentity: React.FC<{ domain: string; name?: string; rank?: number; hideDomain?: boolean }> = ({
  domain,
  name,
  rank,
  hideDomain = false,
}) => (
  <div className="inline-flex items-center gap-2">
    {rank !== undefined && (
      <span className="w-4 shrink-0 text-right text-xs tabular-nums text-zinc-300">{rank}</span>
    )}
    <img
      className="h-5 w-5 shrink-0 rounded-md bg-white"
      src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`}
      alt=""
      width={20}
      height={20}
      loading="lazy"
    />
    <div className="min-w-0">
      <div className="truncate text-sm font-medium text-zinc-900">{name || domain}</div>
      {name && !hideDomain && <div className="truncate text-[11px] text-zinc-400">{domain}</div>}
    </div>
  </div>
);

// ─── Table ────────────────────────────────────────────────────────────────────

export const TableFrame: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`overflow-auto ${className}`}>
    <table className="w-full table-auto border-separate border-spacing-0 text-sm">{children}</table>
  </div>
);

export const Th: React.FC<{ children: React.ReactNode; className?: string; align?: 'left' | 'center' | 'right' }> = ({
  children,
  className = '',
  align = 'center',
}) => (
  <th
    className={`sticky top-0 z-10 border-b border-zinc-200 bg-zinc-50 px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-zinc-400 whitespace-nowrap ${className}`}
    style={{ textAlign: align }}
  >
    {children}
  </th>
);

export const Td: React.FC<{ children: React.ReactNode; className?: string; align?: 'left' | 'center' | 'right' }> = ({
  children,
  className = '',
  align = 'center',
}) => (
  <td className={`border-b border-zinc-200 px-4 py-1.5 align-middle whitespace-nowrap ${className}`} style={{ textAlign: align }}>{children}</td>
);

// ─── Tabs ─────────────────────────────────────────────────────────────────────

export const MiniTabs: React.FC<{
  items: string[];
  active: string;
  onChange: (item: string) => void;
}> = ({ items, active, onChange }) => (
  <div className="inline-flex rounded-lg  bg-zinc-100/80 p-0.5">
    {items.map((item) => (
      <button
        key={item}
        onClick={() => onChange(item)}
        className={`h-7 rounded-md px-3 text-xs font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 ${
          active === item
            ? 'bg-white text-zinc-950 '
            : 'text-zinc-500 hover:text-zinc-800'
        }`}
      >
        {item}
      </button>
    ))}
  </div>
);

// ─── Chart placeholder ────────────────────────────────────────────────────────

export const EmptyChart: React.FC<{ label: string; max?: string }> = ({ label, max = '75%' }) => (
  <div className="h-48 px-5 py-4">
    <div className="mb-2 flex items-center justify-between text-[11px] text-zinc-300">
      <span>0%</span>
      <span>20%</span>
      <span>40%</span>
      <span>{max}</span>
    </div>
    <div className="relative h-32 rounded-lg bg-[linear-gradient(to_right,rgba(226,232,240,0.5)_1px,transparent_1px),linear-gradient(to_bottom,rgba(226,232,240,0.5)_1px,transparent_1px)] bg-[size:25%_25%]">
      <div className="absolute inset-x-6 bottom-8 h-20 rounded-t-full border-t-2 border-dashed border-zinc-200" />
      <div className="absolute bottom-4 left-6 right-6 flex items-end gap-1.5">
        {[18, 32, 26, 44, 39, 58, 51, 64, 59, 68].map((height, index) => (
          <div
            key={index}
            className="flex-1 rounded-t-sm bg-zinc-900/70"
            style={{ height }}
          />
        ))}
      </div>
    </div>
    <div className="mt-3 flex items-center justify-between text-[11px] text-zinc-400">
      <span>{label}</span>
      <span>Showing data for 1 day</span>
    </div>
  </div>
);

export const ToolbarIcons = () => (
  <>
    <IconTool label="Configure columns">
      <span className="text-[11px] font-semibold">Cols</span>
    </IconTool>
    <IconTool label="Chart actions" />
  </>
);